import React from "react";
import track from "react-tracking";

import { Box } from "@metrea-ui/v2";
import { PageHeader } from "../components/SiteLayout/Header";
import { PageFooter } from "../components/SiteLayout/Footer";

const Landing_Page = (props) => {
	return (
		<>
			<PageHeader pageTitle="Home" />
			<Box className="pageContent">
				<article style={{ padding: "1em", margin: "0" }}>
					<div style={{ fontSize: "1.4em", fontWeight: "600", padding: "12px 0" }}>
						Strategy wears a dollar sign
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						“We do not have and probably never will have enough money to buy all the things we
						could effectively use for our defense. The choices we have to make would be difficult
						and painful even if our military budget were twice what it is today. The fact that we
						are dealing with a lesser sum only makes the choices harder and more painful.”
						<br />
						–Bernard Brodie, Strategy in the Missile Age, published in 1959 when the U.S. defense
						budget was $342 billion (in FY 2024 dollars).
					</p>
					<p style={{ padding: "5px 0 10px 0" }}>
						Strategy is about choices, and those choices determine how resources are allocated
						among competing priorities. The defense budget is both a reflection of strategic
						choices and a key reason defense strategy is needed. A strategy set without regard for
						resource constraints risks being inexecutable in practice, and a budget set without
						regard for strategy risks being ineffective and inefficient in how it allocates
						resources.
					</p>
					<p style={{ padding: "5px 0 10px 0" }}>
						The challenge for strategists, planners, and policymakers is being able to iterate
						between defense strategy and budgets to understand how changes in one affect the
						other. Every budget involves thousands of individual choices, and the effects of these
						choices can be lost in the noise. But in aggregate these changes—many of them small
						relative to the overall budget—can lead to subtle but consequential shifts with
						long-term implications.
					</p>
					<div style={{ fontSize: "1.4em", fontWeight: "600", padding: "12px 0" }}>
						Discover Raven
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						Raven is an online tool developed by Metrea Strategic Insights that allows users to
						rapidly simulate changes in strategy and budgets to understand the range of options
						available and their impacts on programs, force structure, and capabilities.
					</p>
					<div style={{ fontSize: "1.4em", fontWeight: "600", padding: "12px 0" }}>
						How it works
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						“The problems of military strategy and force requirements, though complex, can be
						grasped, analyzed, and understood. They can be importantly, even if not wholly,
						quantified. Satisfactory answers can and should be found through a combination of
						judgment and analysis.” <br />
						--Alain C. Enthoven and K. Wayne Smith in How Much Is Enough?, published in 1971
					</p>
					<div style={{ fontSize: "1.1em", fontWeight: "600", padding: "10px 0" }}>
						Build Simulations
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						Users begin by creating a simulation in the tool. Each simulation is based on a
						baseline budget, and all selections made in a simulation are relative to this
						baseline. Raven can hold multiple baselines for many nations simultaneously, including
						allies, partners, and adversaries alike.
					</p>
					<p style={{ padding: "5px 0 10px 0" }}>
						Users select which baseline they want to use when creating a simulation. They can save
						simulations in their account and return to their work later, share simulations with
						other users to collaborate, and copy simulations to create multiple versions and test
						alternative approaches.
					</p>
					<div style={{ fontSize: "1.1em", fontWeight: "600", padding: "10px 0" }}>
						Model the Budget
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						At the heart of Raven is the budget model that estimates the change in annual funding
						and force levels that would result from changes to the baseline. Users can make
						changes to acquisition programs, basing, readiness, numbers of units, and a range of
						other options. The budget model estimates the costs and force structure implications
						of each selection, including research and development, procurement, operation and
						sustainment, and military construction costs. It projects these impacts by year over
						the time horizon of the baseline, typically a five-year planning period. The tool
						includes realistic constraints on how much an item can be altered from the baseline to
						account for industrial base limitations and other factors.
					</p>
					<div style={{ fontSize: "1.1em", fontWeight: "600", padding: "10px 0" }}>
						Measure Strategic Preferences
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						Raven also allows users to adjust overall defense strategy and budget limits. They can
						specify how much to increase or decrease emphasis (relative to current strategy)
						across a range of strategic preferences, including operational preferences, geographic
						preferences, resource balance preferences, and budget limits.
					</p>
					<div style={{ fontSize: "1.1em", fontWeight: "600", padding: "10px 0" }}>
						Run the Algorithm
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						The Raven algorithm is the connection between strategy and budgets. The algorithm
						correlates strategic preferences with specific budgetary decisions, and it is used in
						two ways. The Raven algorithm can be used to automatically recommend a set of changes
						to the budget based on a user’s strategic preferences. Rather than sorting through the
						budget manually looking for items to change, the user can enter the desired shift in
						strategy using the strategic preferences and let the algorithm search for an optimum
						set of choices in the budget. The user can then review the choices the algorithm makes
						and refine them as necessary. The algorithm can also be used in reverse, taking a set
						of proposed changes to the budget and “scoring” what these changes imply in terms of
						shifts in strategic preferences.
					</p>
					<div style={{ fontSize: "1.1em", fontWeight: "600", padding: "10px 0" }}>
						Learn and Evolve
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						The Raven algorithm is built to continuously learn and improve as it is used. For this
						reason, the initial release of Raven has the algorithm set to passive learning mode.
						As more people use the tool, the algorithm builds its understanding of the complex
						interrelationships at play in the strategy and budget. Once the algorithm reaches a
						threshold level of learning, it will be fully activated and made available to users.
					</p>
					<div style={{ fontSize: "1.4em", fontWeight: "600", padding: "12px 0" }}>
						Who can use it
					</div>
					<p style={{ padding: "5px 0 10px 0" }}>
						Anyone can create a free user account in Raven and begin using the tool. The only
						information required is a working email address for login and authentication purposes.
						Free accounts can gain access to additional features by completing challenges and
						earning challenge coins. Challenges are short quizzes that help the algorithm learn
						and understand the relationships between strategic preferences and choices in the
						budget.
					</p>
					<p style={{ padding: "5px 0 10px 0" }}>
						Paid subscriptions are available that provide higher levels of access in the tool,
						including the ability to create private workspaces, unique baselines, and fully
						customizable strategic preferences and budget options. For more information about a
						paid subscription, contact us here.
					</p>
					<br />
				</article>
				<PageFooter />
			</Box>
		</>
	);
};

export const Landing = track(
	{ page: "Landing" },
	{ dispatchOnMount: (contextData) => ({ event: "pageLoaded" }) }
)(Landing_Page);
